import { useCallback, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { ADD_MODAL } from './addModal.ts'
import { Modal, type ModalProps } from './Modal.tsx'

/**
 * 「추가」 창 — 폼 옵션과 본문 portal을 묶는다(작업 · 시료 · 레시피 · 랩웨어 · 사용자 · 보정 기록 · 보충 기록 ·
 * eric 2026-09-30 「창마다 포맷이 달라.. 다 통일해줘」 · 「깔끔한 클로드에서 새로운 거 추가할 때 설정하는 걸로」).
 *
 * 기본 옵션은 `ADD_MODAL` 하나다. 제목·설명·닫힘과 선택적인 `size`만 호출부가 준다.
 * 안의 폼은 공용 조각(`screens/formSections.tsx` — `FormSection` · `FormRow` · `FormHint` · `AddActions`)으로 짓고,
 * 바닥 줄(「취소」 · 「추가」)은 `AddActions` 가 창 바닥으로 옮겨 온다.
 */
export type AddModalProps = {
  /** 「N 추가」 */
  title: string
  /** 제목 오른쪽 같은 줄 보조 표기(예: 웹훅 추가 창의 채널) · 있을 때만 */
  titleAside?: ReactNode | undefined
  /** 제목 밑 한 줄 옅은 설명. 있을 때만 */
  description?: string | undefined
  onClose: () => void
  /**
   * 보내는 중 — × · Esc · 바깥 누름으로 닫히지 않는다. 안의 폼이 `useModalBusy`(`DetailPage busy`)로 알려도 같다.
   * 폼이 창을 직접 여는 곳(사용자 · 보정 · 보충)이 넘긴다
   */
  busy?: boolean | undefined
  /** 폭 이행용. 생략하면 기존 폼 폭을 유지한다 */
  size?: Extract<ModalProps['size'], 'sm' | 'md' | 'lg'> | undefined
  children: ReactNode
}

export function AddModal({
  title,
  titleAside,
  description,
  onClose,
  busy,
  size,
  children,
}: AddModalProps) {
  /**
   * 창을 세울 자리. `undefined` = 아직 못 쟀다(첫 그림 직전 한 번) · `null` = 셸 밖 → 그 자리에 그대로.
   * 🔴 **문서 전체에서 찾지 않고 «이 창을 연 자리의» 셸 본문을 찾는다**(`closest`). 그림 중에 `document` 로 찾으면
   *    레이아웃이 바뀌는 순간(로딩 → 콘솔) 떨어져 나간 옛 셸 본문을 잡아 창이 통째로 사라졌다(2026-09-30 실측).
   */
  const [host, setHost] = useState<HTMLElement | null | undefined>(undefined)
  const anchor = useCallback((el: HTMLSpanElement | null) => {
    if (el !== null) setHost(el.closest<HTMLElement>('.shell__body'))
  }, [])
  const modal = (
    <Modal
      {...ADD_MODAL}
      size={size ?? ADD_MODAL.size}
      busy={busy}
      description={description}
      onClose={onClose}
      title={title}
      titleAside={titleAside}
    >
      <div className="a-addbody">{children}</div>
    </Modal>
  )
  /*
   * 🔴 **덮개는 셸 본문(`.shell__body`) 한 칸을 덮는다** — 머리줄 · 상시 작업 줄(E-STOP)은 밖이다(07 §6).
   *    창을 여는 자리가 셸 본문 «안의 다른 배치 조상»(모니터링 소모품 판처럼 `position` · `overflow` 가 걸린 판)이면
   *    절대 배치 덮개가 그 판에 갇혀 잘린다(2026-09-30 보충 기록 창 실측). 그래서 셸 본문으로 옮겨 세운다.
   *    셸 밖(시험 · 태블릿 · 모듈 앱)에서는 그 자리에 그대로 선다.
   */
  return (
    <>
      <span hidden ref={anchor} />
      {host === undefined ? null : host === null ? modal : createPortal(modal, host)}
    </>
  )
}
